// ---------------------------------------------------------------------------
// Feature Card Renderer
// ---------------------------------------------------------------------------
// Marketing primitive — publishes the editor `FeatureCard` block as a
// grid of bullet/title/description tiles parsed from the same pipe-
// delimited textarea the Puck editor uses ("Title | Description" per
// line). Distinct from `feature-grid`, which is the data-driven array
// variant used in long-form landing pages.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

type FeatureVariant = "cards" | "minimal";

interface FeatureCardProps {
  items?: string;
  columns?: string | number;
  variant?: FeatureVariant | string;
}

const VALID_VARIANTS: ReadonlySet<FeatureVariant> = new Set(["cards", "minimal"]);
const MAX_COLUMNS = 4;
const MIN_COLUMNS = 1;

function resolveVariant(value: unknown): FeatureVariant {
  if (typeof value === "string" && VALID_VARIANTS.has(value as FeatureVariant)) {
    return value as FeatureVariant;
  }
  return "cards";
}

function resolveColumns(value: unknown): number {
  const raw =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : NaN;
  if (!Number.isFinite(raw)) return 2;
  return Math.max(MIN_COLUMNS, Math.min(Math.trunc(raw), MAX_COLUMNS));
}

interface Feature {
  title: string;
  description: string;
}

function parseItems(value: unknown): Feature[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [titlePart, descPart] = line.split("|");
      return {
        title: (titlePart ?? "").trim(),
        description: (descPart ?? "").trim(),
      };
    })
    .filter((f) => f.title.length > 0);
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as FeatureCardProps;

  const features = parseItems(p.items);
  if (features.length === 0) {
    return "<!-- feature-card missing items -->";
  }

  const variant = resolveVariant(p.variant);
  const columns = resolveColumns(p.columns);

  const cards = features
    .map(
      (f) => `      <li class="ease-feature-card__item">
        <h3 class="ease-feature-card__title">${text(f.title)}</h3>${f.description ? `\n        <p class="ease-feature-card__description">${text(f.description)}</p>` : ""}
      </li>`,
    )
    .join("\n");

  return `<section class="ease-section ease-feature-card ease-feature-card--${attr(variant)}">
    <ul class="ease-feature-card__grid" style="--ease-feature-card-columns: ${attr(String(columns))};" data-columns="${attr(String(columns))}">
${cards}
    </ul>
</section>`;
}

registerRenderer("feature-card", { render });
